import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, matchPath, useNavigate } from 'react-router-dom';
import useToken from 'hooks/useToken';
import SupportEngineSidebar from './SupportEngineSidebar';
import SupportEngineSettingsSidebar from './SupportEngineSettingsSidebar';
import SupportEngineNavbar from './SupportEngineNavbar';
import NotificationPermissionBanner from '../components/NotificationPermissionBanner';
import SupportInteractiveToast from '../components/SupportInteractiveToast';
import useSupportGlobalHub from '../hooks/useSupportGlobalHub';
import DashboardView from '../views/DashboardView';
import TicketsView from '../views/TicketsView';
import AgentsView from '../views/AgentsView';
import SettingsView from '../views/SettingsView';

const SupportEngineLayout = ({ onInitialReady }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useToken();
  
  const match = matchPath({ path: "/support-engine/ticket/:id" }, location.pathname);
  const isValidParamId = match?.params?.id && match.params.id !== 'undefined';

  const [activeTab, setActiveTab] = useState('tickets');
  const [settingsSubTab, setSettingsSubTab] = useState('atajos');
  const [selectedTicket, setSelectedTicket] = useState(
    isValidParamId ? { id: match.params.id, idTicket: match.params.id } : null
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Si entra a una pestaña distinta a tickets por URL directa, liberar el loader
  useEffect(() => {
    if (activeTab !== 'tickets' && onInitialReady) {
      onInitialReady();
    }
  }, [activeTab, onInitialReady]);

  // Manejador para abrir un ticket directamente al hacer clic en el Toast interactivo o notificación
  const handleOpenTicketFromToast = useCallback((idTicket) => {
    setActiveTab('tickets');
    setSelectedTicket({ id: idTicket, idTicket: idTicket });
    navigate(`/support-engine/ticket/${idTicket}`);
  }, [navigate]);

  // Hub global de SignalR y notificaciones (toasts interactivos + notificaciones nativas de Windows)
  const { incomingToast, dismissToast } = useSupportGlobalHub({
    token,
    selectedTicketId: selectedTicket?.idTicket || selectedTicket?.id,
    onNavigateToTicket: handleOpenTicketFromToast
  });

  useEffect(() => {
    const rawPath = location.pathname.toLowerCase().replace(/\/$/, '');
    const m = matchPath({ path: "/support-engine/ticket/:id" }, location.pathname);

    if (m && m.params.id && m.params.id !== 'undefined') {
      setActiveTab('tickets');
      const paramId = m.params.id;
      setSelectedTicket(prev => {
        const prevId = prev?.idTicket || prev?.id;
        if (String(prevId) !== String(paramId)) {
          return { id: paramId, idTicket: paramId };
        }
        return prev;
      });
    } else if (rawPath === '/support-engine/tickets') {
      setActiveTab('tickets');
      setSelectedTicket(null);
    } else if (rawPath === '/support-engine/dashboard') {
      setActiveTab('dashboard');
      setSelectedTicket(null);
    } else if (rawPath === '/support-engine/agents') {
      setActiveTab('agents');
      setSelectedTicket(null);
    } else if (rawPath.startsWith('/support-engine/settings')) {
      setActiveTab('settings');
      setSelectedTicket(null);
    } else if (rawPath === '/support-engine') {
      // Redirigir la raíz /support-engine a la lista de tickets por defecto
      navigate('/support-engine/tickets', { replace: true });
    }
  }, [location.pathname, navigate]);

  const handleTabChange = tab => {
    setActiveTab(tab);
    setSelectedTicket(null);
    setMobileMenuOpen(false);

    if (tab === 'tickets') {
      navigate('/support-engine/tickets');
    } else if (tab === 'dashboard') {
      navigate('/support-engine/dashboard');
    } else if (tab === 'agents') {
      navigate('/support-engine/agents');
    } else if (tab === 'settings') {
      setSettingsSubTab('atajos');
      navigate('/support-engine/settings');
    }
  };

  const handleUpdateSelectedTicket = (newData) => {
    setSelectedTicket(prev => {
      if (!prev) return newData;
      return { ...prev, ...newData };
    });
  };

  const renderActiveContent = () => {
    switch (activeTab) {
      case 'tickets':
        return (
          <TicketsView
            selectedTicket={selectedTicket}
            setSelectedTicket={(t) => {
              setSelectedTicket(t);
              if (t) {
                const targetId = t.idTicket || t.id;
                if (targetId && String(targetId) !== 'undefined') {
                  navigate(`/support-engine/ticket/${targetId}`);
                }
              } else {
                navigate('/support-engine/tickets');
              }
            }}
            onTicketDetailLoaded={handleUpdateSelectedTicket}
            onInitialReady={onInitialReady}
          />
        );
      case 'agents':
        return <AgentsView />;
      case 'settings':
        return <SettingsView activeSubTab={settingsSubTab} setActiveSubTab={setSettingsSubTab} />;
      case 'dashboard':
      default:
        return <DashboardView />;
    }
  };

  return (
    <div className="support-engine-app">
      {/* Backdrop oscuro para móvil */}
      {mobileMenuOpen && (
        <div
          className="se-sidebar-backdrop d-lg-none"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar Principal (en móvil es offcanvas drawer) */}
      <SupportEngineSidebar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      {/* Sub-Sidebar de Configuración (abre hacia adelante en desktop) */}
      {activeTab === 'settings' && (
        <SupportEngineSettingsSidebar
          activeSubTab={settingsSubTab}
          setActiveSubTab={setSettingsSubTab}
        />
      )}

      {/* Main Container */}
      <div className="se-main">
        <SupportEngineNavbar
          activeTab={activeTab}
          settingsSubTab={settingsSubTab}
          selectedTicket={selectedTicket}
          onUpdateTicket={handleUpdateSelectedTicket}
          onToggleMobileMenu={() => setMobileMenuOpen(prev => !prev)}
        />

        <NotificationPermissionBanner />

        <div className="se-body">
          {renderActiveContent()}
        </div>
      </div>

      {/* Notificación flotante interactiva de nuevos mensajes estilo WhatsApp */}
      <SupportInteractiveToast
        toast={incomingToast}
        onOpenTicket={handleOpenTicketFromToast}
        onDismiss={dismissToast}
      />
    </div>
  );
};

export default SupportEngineLayout;
